if (!customElements.get('our-promises-timeline')) {
  class OurPromisesTimeline extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.steps = Array.from(this.querySelectorAll('[data-promise-step]'));
      if (!this.steps.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.triggers = this.steps.map(step => step.querySelector('[data-promise-trigger]'));
      this.copies = this.steps.map(step => step.querySelector('[data-promise-copy]'));
      this.images = Array.from(this.querySelectorAll('[data-promise-image]'));
      this.images.forEach(image => {
        const photo = image.querySelector('img');
        if (photo) photo.loading = 'eager';
      });
      this.navigation = this.querySelector('[data-promise-steps]');
      this.toggle = this.querySelector('[data-promise-toggle]');
      this.status = this.querySelector('[data-promise-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && this.steps.length > 1;
      this.duration = Math.max(8, Math.min(30, Number(this.dataset.interval) || 15)) * 1000;
      this.userPaused = !this.autoplay || this.motion.matches || Boolean(window.Shopify?.designMode);
      this.hovered = false;
      this.focused = false;
      this.inView = !('IntersectionObserver' in window);
      this.index = 0;
      this.elapsed = 0;
      this.previousTime = null;
      this.frame = null;
      this.tick = this.tick.bind(this);
      this.setAttribute('data-enhanced', '');

      this.triggers.forEach((trigger, index) => {
        trigger.disabled = false;
        trigger.addEventListener('click', () => this.select(index, true), options);
        trigger.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this.steps.length;
          if (event.key === 'ArrowUp') next = (index + this.steps.length - 1) % this.steps.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.steps.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next, true);
          this.triggers[next].focus();
        }, options);
      });

      this.navigation.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.sync();
      }, options);
      this.navigation.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.sync();
      }, options);
      this.navigation.addEventListener('focusin', () => {
        this.focused = true;
        this.sync();
      }, options);
      this.navigation.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.focused = this.navigation.contains(document.activeElement);
          this.sync();
        });
      }, options);
      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch' || event.target.closest('[data-promise-toggle]')) return;
        this.userPaused = true;
        this.sync();
      }, options);

      if (this.toggle) {
        this.toggle.hidden = !this.autoplay;
        this.toggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.sync();
        }, options);
      }
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.paintProgress();
        this.sync();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const index = this.steps.findIndex(step => step.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.select(index, true);
      }, options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
          this.sync();
        }, { threshold: [0, .15] });
        this.observer.observe(this);
      }
      this.select(0);
    }

    select(index, manual = false) {
      this.index = index;
      this.elapsed = 0;
      this.previousTime = null;
      if (manual) this.userPaused = true;
      const selectedId = this.steps[index].dataset.blockId;
      this.steps.forEach((step, i) => {
        const active = i === index;
        step.classList.toggle('is-active', active);
        this.triggers[i].setAttribute('aria-expanded', String(active));
        this.copies[i].hidden = !active;
      });
      let hasImage = false;
      this.images.forEach(image => {
        const active = image.dataset.blockId === selectedId;
        image.classList.toggle('is-active', active);
        image.setAttribute('aria-hidden', String(!active || image.hasAttribute('data-image-empty')));
        if (active && !image.hasAttribute('data-image-empty')) hasImage = true;
      });
      this.classList.toggle('is-imageless', !hasImage);
      this.dataset.activeIndex = String(index);
      if (manual && this.status) {
        const title = this.triggers[index].querySelector('[data-promise-title]').textContent.trim();
        this.status.textContent = `${index + 1} / ${this.steps.length}: ${title}`;
      }
      this.paintProgress();
      this.sync();
    }

    canRun() {
      return this.autoplay && !this.userPaused && !this.hovered && !this.focused && this.inView && document.visibilityState !== 'hidden';
    }

    sync() {
      if (!this.controller) return;
      if (this.toggle) {
        this.toggle.dataset.paused = String(this.userPaused);
        const label = this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
        this.toggle.querySelector('[data-promise-toggle-label]').textContent = label;
        this.toggle.setAttribute('aria-label', label);
        this.toggle.title = label;
      }
      const running = this.canRun();
      this.dataset.running = String(running);
      if (!running) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.previousTime = null;
      } else if (this.frame === null) {
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    paintProgress() {
      const progress = !this.autoplay || this.motion.matches ? 1 : Math.min(this.elapsed / this.duration, 1);
      this.style.setProperty('--promise-progress', String(progress));
    }

    tick(time) {
      this.frame = null;
      if (!this.canRun()) {
        this.previousTime = null;
        return;
      }
      if (this.previousTime !== null) this.elapsed += time - this.previousTime;
      this.previousTime = time;
      if (this.elapsed >= this.duration) {
        this.select((this.index + 1) % this.steps.length);
        return;
      }
      this.paintProgress();
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.controller = null;
    }
  }
  customElements.define('our-promises-timeline', OurPromisesTimeline);
}
